/**
 * Small SVG charts.
 *
 * Hand-rolled rather than pulling in a charting library: the console needs a
 * line and a bar list, and a charting dependency would be larger than the
 * whole rest of this app and would need theming back to the brand palette
 * anyway.
 */

export interface Series {
  label: string;
  color: string;
  values: number[];
}

export function LineChart({ labels, series, height = 210 }: { labels: string[]; series: Series[]; height?: number }) {
  const width = 760;
  const pad = { top: 12, right: 12, bottom: 24, left: 44 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const max = Math.max(1, ...series.flatMap((s) => s.values));

  /**
   * Round the axis up to something readable, then choose ticks that are whole
   * numbers and distinct.
   *
   * The naive version — five fractions of the maximum — collapses when the
   * maximum is small: a max of 1 produced ticks of 0, 0, 0, 1, 1, which drew
   * gridlines on top of each other and labelled the axis twice with the same
   * number. Counts are integers, so the axis should be too.
   */
  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.max(1, Math.ceil(max / magnitude) * magnitude);
  const divisions = Math.min(4, top);
  const ticks = Array.from({ length: divisions + 1 }, (_, i) => Math.round((top / divisions) * i)).filter(
    (v, i, a) => a.indexOf(v) === i,
  );

  const n = labels.length;
  const x = (i: number) => pad.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v: number) => pad.top + innerH - (v / top) * innerH;

  return (
    <>
      <svg className="chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeWidth="1" />
            <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" fontSize="10" fill="var(--ink-faint)">
              {t >= 1000 ? `${Math.round(t / 1000)}k` : t}
            </text>
          </g>
        ))}

        {series.map((s) => {
          const d = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
          const area = `${d} L ${x(n - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`;
          return (
            <g key={s.label}>
              <path d={area} fill={s.color} opacity="0.08" />
              <path d={d} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            </g>
          );
        })}

        {labels.map((l, i) =>
          // Only ~6 labels fit; thin them rather than overlapping.
          i % Math.max(1, Math.ceil(n / 6)) === 0 ? (
            <text key={l} x={x(i)} y={height - 6} textAnchor="middle" fontSize="10" fill="var(--ink-faint)">
              {l.slice(5)}
            </text>
          ) : null,
        )}
      </svg>

      <div className="chart-legend">
        {series.map((s) => (
          <span className="chart-legend__key" key={s.label}>
            <span className="chart-legend__swatch" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </>
  );
}

export function BarList({ rows }: { rows: Array<{ label: string; value: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="stack" style={{ gap: 10 }}>
      {rows.map((r) => (
        <div className="bar-row" key={r.label}>
          <span className="bar-row__label" title={r.label}>
            {r.label}
          </span>
          <span className="bar-row__track">
            <span className="bar-row__fill" style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className="bar-row__value">{r.value}</span>
        </div>
      ))}
    </div>
  );
}
